import { CFDFile } from '@aero-platform/shared'
import { fetchSimTemplatesForSubmission } from '../../../api/submission-API'

const MAX_RESULTS = 10

/**
 * Parse a raw sim-templates API response into a flat CFDFile options array.
 * Templates are filtered client-side by the query string.
 * Candidates (database search results) are only included when includeCandidates is true.
 */
const parseSimTemplateResponse = (data: any, query: string, includeCandidates: boolean): CFDFile[] => {
  const opts: CFDFile[] = []
  const q = query.toLowerCase()

  // Methodology / override templates — always included, filtered client-side by system label
  if (data?.templates?.length) {
    data.templates.forEach((t: CFDFile) => {
      if (opts.length < MAX_RESULTS && (!q || (t.file || '').toLowerCase().includes(q))) {
        opts.push(t)
      }
    })
  }

  // Completed-case candidates — only included when the user has typed a search query
  if (includeCandidates && data?.candidates?.length) {
    data.candidates.forEach((c: CFDFile) => {
      if (opts.length < MAX_RESULTS) {
        opts.push(c)
      }
    })
  }

  return opts
}

/**
 * Fetch methodology/override templates for a submission (no database search).
 * Only returns templates that belong to this run's methodology or are overrides.
 */
export const fetchAllSimTemplates = async (
  doFetch: any,
  submissionId: string,
  submissionName: string
): Promise<CFDFile[]> => {
  const resp: any = await fetchSimTemplatesForSubmission(doFetch, submissionId, submissionName)
  const data = resp?.data || resp
  return parseSimTemplateResponse(data, '', false)
}

/**
 * Fetch sim template options filtered by a user search query.
 * Includes both methodology templates and database candidates matching the query.
 */
export const fetchSimTemplatesByQuery = async (
  doFetch: any,
  submissionId: string,
  submissionName: string,
  query: string
): Promise<CFDFile[]> => {
  if (!query.trim()) {
    return fetchAllSimTemplates(doFetch, submissionId, submissionName)
  }
  const resp: any = await fetchSimTemplatesForSubmission(doFetch, submissionId, submissionName, query)
  const data = resp?.data || resp
  return parseSimTemplateResponse(data, query, true)
}
